import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isContentLocale, resolveMovieTitle, type Movie } from '@filmsloth/shared';
import { en, type TranslationKey } from './en.js';
import { de } from './de.js';
import { brandedKey } from '../lib/brandedKey.js';

export type Translations = Record<TranslationKey, string>;

/**
 * Locale registry. Adding a language means dropping a file next to `en.ts`
 * that exports the same keys and registering it here — nothing else changes.
 */
export const locales: Record<string, Translations> = {
  en,
  de,
};

export type LocaleCode = keyof typeof locales;

export const DEFAULT_LOCALE: LocaleCode = 'en';

/** Language names in their own language: a switcher nobody has to translate. */
export const LOCALE_NAMES: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
};

const STORAGE_KEY = brandedKey('locale');

export function isLocaleCode(value: unknown): value is LocaleCode {
  // `hasOwn`, not `in`: `'toString' in locales` is true, and would hand the
  // provider a function where a dictionary is expected.
  return typeof value === 'string' && Object.hasOwn(locales, value);
}

/**
 * Picks a locale from what the browser advertises.
 *
 * `navigator.languages` is ordered by preference, so the first entry we can
 * actually serve wins. Region subtags are dropped — `de-AT` and `de-CH` get
 * German, which is far closer than falling through to English.
 */
export function detectLocale(languages: readonly string[]): LocaleCode {
  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLocaleCode(base)) return base;
  }
  return DEFAULT_LOCALE;
}

/**
 * An explicit choice outranks the browser, and outlives the tab. Reading
 * storage can throw outright in private mode, so every access is guarded and
 * failure just means falling back to detection.
 */
export function initialLocale(): LocaleCode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocaleCode(stored)) return stored;
  } catch {
    // Storage unavailable — detection below is a fine answer.
  }
  try {
    return detectLocale(navigator.languages ?? [navigator.language]);
  } catch {
    return DEFAULT_LOCALE;
  }
}

type Vars = Record<string, string | number>;
export type Translate = (key: TranslationKey, vars?: Vars) => string;

interface I18nValue {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: Translate;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<LocaleCode>(initialLocale);

  // Keep the choice, and keep `<html lang>` honest: screen readers and the
  // browser's own translation prompt both read it.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Private mode or a full quota: the choice lasts for this tab only.
    }
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const dictionary = locales[locale] ?? en;
    return {
      locale,
      setLocale,
      t: (key, vars) => interpolate(dictionary[key] ?? en[key] ?? key, vars),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}

export function useT(): Translate {
  return useI18n().t;
}

/**
 * Genre labels come from the dictionary, not from `GENRE_LABELS` in `shared`:
 * the ids are shared domain data, their wording is presentation.
 */
export function useGenreLabel(): (genre: string) => string {
  const t = useT();
  return (genre) => t(`genre.${genre}` as TranslationKey);
}

/**
 * The title to render for a movie in the viewer's current UI locale.
 *
 * UI locales and catalog content locales share the same codes today, so the
 * UI locale doubles as the content locale directly; a locale the catalog has
 * no title for (or a UI locale, if one is ever added, that isn't a content
 * locale at all) just falls back to `movie.title`.
 */
export function useMovieTitle(): (movie: Movie) => string {
  const { locale } = useI18n();
  return (movie) => (isContentLocale(locale) ? resolveMovieTitle(movie, locale) : movie.title);
}

/** A 0–10 rating with one decimal, written the way the viewer's locale writes numbers. */
export function useRating(): (rating: number) => string {
  const { locale } = useI18n();
  return (rating) =>
    rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
